import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { EventSummaryResponse } from '../utils/types';
import { apiGet, ApiError } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const EventsList: React.FC = () => {
  const [events, setEvents] = useState<EventSummaryResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [filter, setFilter] = useState<string>('all');
  const { token, user } = useAuth();

  // Check if current user has organizer role (case-insensitive)
  const isOrganizer = user?.roles?.some(role => role.toUpperCase() === 'ORGANIZER') || false;

  useEffect(() => {
    loadEvents();
  }, [token]);

  const loadEvents = async () => {
    if (!token) return;
    
    setLoading(true);
    setError('');
    
    try {
      const eventsData = await apiGet<EventSummaryResponse[]>('/events', token);
      setEvents(Array.isArray(eventsData) ? eventsData : []);
    } catch (err) {
      console.error('Failed to load events:', err);
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Failed to load events');
      }
      setEvents([]); // Ensure events is always an array
    } finally {
      setLoading(false);
    }
  };

  const filteredEvents = (events || []).filter(event => {
    if (filter === 'all') return true;
    return event.state === filter;
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStateEmoji = (state: string) => {
    switch (state) {
      case 'draft': return '📝';
      case 'open': return '🟢';
      case 'judging': return '⚖️';
      case 'completed': return '✅';
      default: return '❓';
    }
  };

  if (loading) {
    return (
      <div className="container" style={{ textAlign: 'center', padding: '40px' }}>
        <h2>Loading events...</h2>
      </div>
    );
  }

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <h1>Hackathon Events</h1>
        {isOrganizer && (
          <Link to="/create-event" className="btn">
            Create New Event
          </Link>
        )}
      </div>

      {error && (
        <div className="alert alert-error" style={{ marginBottom: '20px' }}>
          <p>Error: {error}</p>
          <button 
            onClick={loadEvents} 
            className="btn btn-secondary" 
            style={{ marginLeft: '10px' }}
          >
            Retry
          </button>
        </div>
      )}

      <div style={{ marginBottom: '20px' }}>
        <label htmlFor="filter" style={{ marginRight: '10px' }}>Filter by state:</label>
        <select 
          id="filter"
          value={filter} 
          onChange={(e) => setFilter(e.target.value)}
          style={{ padding: '5px' }}
        >
          <option value="all">All Events</option>
          <option value="draft">Draft</option>
          <option value="open">Open</option>
          <option value="judging">Judging</option>
          <option value="completed">Completed</option>
        </select>
      </div>

      {filteredEvents.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
          <h3>No events found</h3>
          <p>
            {filter === 'all' 
              ? "There are no events yet." + (isOrganizer ? " Create your first event to get started!" : " Check back later when events are available!")
              : `No events with state "${filter}" found.`
            }
          </p>
          {isOrganizer && (
            <Link to="/create-event" className="btn" style={{ marginTop: '15px' }}>
              Create First Event
            </Link>
          )}
        </div>
      ) : (
        <div className="grid">
          {filteredEvents.map((event) => (
            <div key={event.event_id} className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                <h3 style={{ margin: 0 }}>
                  <Link 
                    to={`/events/${event.event_id}`}
                    style={{ textDecoration: 'none', color: 'inherit' }}
                  >
                    {event.name}
                  </Link>
                </h3>
                <span className={`badge badge-${event.state}`}>
                  {getStateEmoji(event.state)} {event.state}
                </span>
              </div>
              
              <p style={{ color: '#666', marginBottom: '15px', fontSize: '14px' }}>
                {event.description && event.description.length > 120 
                  ? `${event.description.substring(0, 120)}...` 
                  : event.description || 'No description available'
                }
              </p>
              
              <div style={{ fontSize: '12px', color: '#888' }}>
                <div><strong>Registration:</strong> {formatDate(event.registration_deadline)}</div>
                <div><strong>Submission:</strong> {formatDate(event.submission_deadline_overview)}</div>
                <div><strong>Media:</strong> {formatDate(event.submission_deadline_media)}</div>
              </div>
              
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginTop: '15px',
                paddingTop: '15px',
                borderTop: '1px solid #eee'
              }}>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  👥 {event.team_count || 0} teams • 
                  👤 {event.participant_count || 0} participants
                </div>
                <div style={{ display: 'flex', gap: '5px' }}>
                  {isOrganizer && event.state === 'draft' && (
                    <Link 
                      to={`/events/${event.event_id}/edit`}
                      className="btn btn-secondary"
                      style={{ fontSize: '12px', padding: '5px 10px' }}
                    >
                      ✏️ Edit
                    </Link>
                  )}
                  <Link 
                    to={`/events/${event.event_id}`}
                    className="btn"
                    style={{ fontSize: '12px', padding: '5px 10px' }}
                  >
                    View Details
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EventsList;
